import React, { useEffect, useState } from "react";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MonthCalendarGrid, { buildMonthDays } from "./MonthCalendarGrid";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import "./newAppointment.css";
import "./packageFlow.css";

// Custom date field replacing the native <input type="date"> that used to sit on "Datum
// početka"/"Datum plaćanja" — the browser renders that input's day/month/year segments with its
// own internal font, which never matched "Datum završetka" (a plain read-only text input using
// our own moment-formatted string, styled by .hf-pkg-input like any other field). Both the
// trigger here and "Datum završetka" now render through the exact same .hf-pkg-input styling and
// "DD.MM.YYYY" format, and the calendar itself reuses MonthCalendarGrid — the same modern
// nav-header/weekday-row/day-grid already used by NewAppointmentFlow's date step and the Mobile
// Calendar's month-jump popup — instead of the browser's own dated-looking native picker.
const DateField = ({ id, label, value, onChange, allowClear = false }) => {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => moment(value || undefined));
  useLockBodyScroll(open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const selectedDate = value && moment(value).isValid() ? moment(value) : null;

  const handleOpen = () => {
    setViewMonth(selectedDate ? selectedDate.clone() : moment());
    setOpen(true);
  };

  const handleSelectDay = (day) => {
    onChange(day.format("YYYY-MM-DD"));
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        id={id}
        className="hf-pkg-input hf-pkg-datefield-trigger"
        onClick={handleOpen}
      >
        <span>{selectedDate ? selectedDate.format("DD.MM.YYYY") : "—"}</span>
        <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
      </button>

      {open && (
        <div
          className="hf-pkg-overlay hf-pkg-overlay-stacked"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="hf-pkg-modal hf-pkg-datefield-modal">
            <div className="hf-pkg-header">
              <div className="hf-pkg-header-row">
                <h2 className="hf-pkg-title">{label || "Izaberite datum"}</h2>
              </div>
              <button
                type="button"
                className="hf-pkg-close"
                onClick={() => setOpen(false)}
                aria-label="Zatvori"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            </div>

            <MonthCalendarGrid
              viewMonth={viewMonth}
              onPrevMonth={() => setViewMonth((m) => m.clone().subtract(1, "month"))}
              onNextMonth={() => setViewMonth((m) => m.clone().add(1, "month"))}
              monthDays={buildMonthDays(viewMonth)}
              today={moment()}
              selectedDate={selectedDate}
              onSelectDay={handleSelectDay}
            />

            <div className="hf-pkg-datefield-footer">
              <button type="button" onClick={() => handleSelectDay(moment())}>
                Danas
              </button>
              {allowClear && (
                <button
                  type="button"
                  onClick={() => {
                    onChange("");
                    setOpen(false);
                  }}
                >
                  Obriši
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DateField;
